import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useAuth } from "@/providers/auth-provider";
import { loginUser, isMfaRequired, type LoginRequest, type LoginResult } from "../api/auth.api";
import { ApiError } from "@/lib/api/client";
import { resolvePostLoginPath } from "../post-login-redirect";

/**
 * Mutation hook for user login.
 *
 * On success without MFA: stores the access key and navigates on, either to
 * /apps or to whatever page sent the user here to sign in.
 * On success with MFA required: leaves the result in `mutation.data` so the
 * login page can run the MFA step.
 * On error: returns the ApiError for the form to display.
 */
export function useLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };

  return useMutation<LoginResult, ApiError, LoginRequest>({
    mutationFn: loginUser,
    onSuccess: (data) => {
      if (isMfaRequired(data)) {
        return;
      }
      login(data.accessKey);
      navigate({ to: resolvePostLoginPath(search.redirect) });
    },
  });
}
